import firebaseConfig from "@/configs/firebaseConfig"
import analyticsConstants from "@/constants/analyticsConstants"
import getFirebaseApp from "@/helpers/firebaseService"
import {
  getAnalytics,
  initializeAnalytics,
  isSupported,
  logEvent,
  setAnalyticsCollectionEnabled,
  setDefaultEventParameters,
  setUserId,
  setUserProperties
} from "firebase/analytics"

let analytics = null
let initPromise = null

const isAnalyticsDebugMode = () => {
  // Only local `vite` dev server — not deployed staging/dev/prod builds.
  return import.meta.env.DEV
}

const hasMeasurementId = () => {
  return Boolean(firebaseConfig?.firebaseApp?.measurementId)
}

const isAnalyticsCollectionEnabled = () => {
  return firebaseConfig?.analyticsCollectionEnabled === true
}

const getAppEnvironment = () => {
  return import.meta.env.VITE_DD_ENV?.toString?.()?.trim?.() || "unknown"
}

const getDefaultEventParameters = () => ({
  [analyticsConstants.ANALYTICS_EVENT_PARAMS.APP_ENVIRONMENT]:
    getAppEnvironment()
})

const getPageTitle = (to) => {
  const metaTitle = to?.meta?.title?.toString?.()?.trim?.() || ""
  const routeName = to?.name?.toString?.()?.trim?.() || ""
  return metaTitle || routeName || document.title || ""
}

const createAnalyticsInstance = (app) => {
  if (isAnalyticsDebugMode()) {
    // initializeAnalytics accepts config; getAnalytics(app, options) does not.
    return initializeAnalytics(app, {
      config: {
        debug_mode: true
      }
    })
  }

  return getAnalytics(app)
}

const applyDefaultEventParameters = (instance) => {
  setDefaultEventParameters(getDefaultEventParameters())
  return instance
}

const initAnalytics = () => {
  if (initPromise) return initPromise

  initPromise = (async () => {
    if (analytics) return analytics
    if (!hasMeasurementId() || !isAnalyticsCollectionEnabled()) return null

    try {
      const supported = await isSupported()
      if (!supported) return null

      analytics = createAnalyticsInstance(getFirebaseApp())
      setAnalyticsCollectionEnabled(analytics, isAnalyticsCollectionEnabled())
      applyDefaultEventParameters(analytics)
      return analytics
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to initialize Firebase Analytics:", error)
      analytics = null
      return null
    }
  })()

  return initPromise
}

const setUserRole = (userRole) => {
  void setUserRoleAsync(userRole)
}

const setUserRoleAsync = async (userRole) => {
  const role = userRole?.toString?.()?.trim?.()
  if (!role) return

  const instance = analytics || (await initAnalytics())
  if (!instance) return

  try {
    setUserProperties(instance, {
      [analyticsConstants.ANALYTICS_USER_PROPERTIES.USER_ROLE]: role
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to set Firebase Analytics user role:", error)
  }
}

const clearUserIdentity = () => {
  void clearUserIdentityAsync()
}

const clearUserIdentityAsync = async () => {
  const instance = analytics || (await initAnalytics())
  if (!instance) return

  try {
    setUserId(instance, null)
    setUserProperties(instance, {
      [analyticsConstants.ANALYTICS_USER_PROPERTIES.USER_ROLE]: null
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to clear Firebase Analytics user identity:", error)
  }
}

const logPageView = (to) => {
  void logPageViewAsync(to)
}

const logPageViewAsync = async (to) => {
  const instance = analytics || (await initAnalytics())
  if (!instance) return

  const pageViewParams = {
    page_title: getPageTitle(to),
    page_location: window.location.href,
    page_path: to?.fullPath || window.location.pathname,
    ...getDefaultEventParameters()
  }

  if (isAnalyticsDebugMode()) {
    pageViewParams.debug_mode = true
  }

  try {
    logEvent(instance, "page_view", pageViewParams)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to log Firebase Analytics page view:", error)
  }
}

const getAnalyticsInstance = () => analytics

const __resetAnalyticsForTests = () => {
  analytics = null
  initPromise = null
}

export default {
  initAnalytics,
  setUserRole,
  setUserRoleAsync,
  clearUserIdentity,
  clearUserIdentityAsync,
  logPageView,
  logPageViewAsync,
  getAnalyticsInstance,
  __resetAnalyticsForTests
}
